import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Button, CircularProgress, Grid, MenuItem, Rating, Stack, Stepper, Step, StepLabel,
  TextField, Typography,
} from "@mui/material";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/client";
import { PageHeader, SectionCard } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { useAuth } from "../../context/AuthContext";
import { sortByLabel } from "../../utils/sortOptions";
import RichTextEditor from "../common/RichTextEditor";

const STEPS = ["Self Ratings", "Accomplishments", "Growth", "Aspirations"];

const RATING_FIELDS = [
  { key: "workQuality", label: "Work Quality" },
  { key: "productivity", label: "Productivity" },
  { key: "communicationSkills", label: "Communication" },
  { key: "teamwork", label: "Teamwork" },
  { key: "problemSolvingSkills", label: "Problem Solving" },
  { key: "attendancePunctuality", label: "Attendance & Punctuality" },
];

const WORK_STYLES = ["collaborative", "independent", "hybrid", "structured", "flexible"];

function isCycleExpired(cycle) {
  if (!cycle) return true;
  if (cycle.status === "closed") return true;
  if (!cycle.endDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(cycle.endDate);
  end.setHours(0, 0, 0, 0);
  return end < today;
}

export default function SelfAssessmentFormNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [attempted, setAttempted] = useState(false);
  const [empIdError, setEmpIdError] = useState("");
  const [reviewCycles, setReviewCycles] = useState([]);
  const [cyclesLoading, setCyclesLoading] = useState(true);
  const [form, setForm] = useState({
    employeeID: "", assessmentPeriod: "",
    workQuality: 0, productivity: 0, communicationSkills: 0, teamwork: 0,
    problemSolvingSkills: 0, attendancePunctuality: 0,
    achievements: "", strengths: "", learningActivities: "",
    skillsToImprove: "", challengesFaced: "", selfImprovementEfforts: "", supportNeeded: "",
    shortTermGoals: "", longTermGoals: "", careerAspirations: "",
    preferredWorkStyle: "hybrid", feedbackOnManagement: "", employeeComments: "",
    overallRating: 0,
  });
  const [loading, setLoading] = useState(false);

  const myEmpId = user?.employeeid || user?.employee?.employeeid || "";
  const myName = user?.name || user?.employee?.name || "";
  const employeeDisplay = useMemo(() => {
    if (myName && myEmpId) return `${myName} (${myEmpId})`;
    return myEmpId || myName || "";
  }, [myName, myEmpId]);

  const selectedCycle = useMemo(
    () => reviewCycles.find((c) => c.name === form.assessmentPeriod) || null,
    [reviewCycles, form.assessmentPeriod]
  );
  const ratingScale = selectedCycle?.ratingScale > 0 ? selectedCycle.ratingScale : 5;

  const clampRatingsToScale = (draft, scale) => {
    const next = { ...draft };
    [...RATING_FIELDS.map((f) => f.key), "overallRating"].forEach((key) => {
      if (next[key] > scale) next[key] = scale;
    });
    return next;
  };

  const handlePeriodChange = (periodName) => {
    const cycle = reviewCycles.find((c) => c.name === periodName);
    const scale = cycle?.ratingScale > 0 ? cycle.ratingScale : 5;
    setForm((f) => clampRatingsToScale({ ...f, assessmentPeriod: periodName }, scale));
  };

  // Self-assessment is always for the logged-in employee
  useEffect(() => {
    if (myEmpId) setForm((f) => ({ ...f, employeeID: myEmpId }));
  }, [myEmpId]);

  // Load all review cycles for assessment period dropdown
  useEffect(() => {
    setCyclesLoading(true);
    api.get("/api/performance/cycles")
      .then((res) => {
        const all = Array.isArray(res.data?.data) ? res.data.data : [];
        // Only activated (and not expired) cycles are open for self-assessment.
        const openCycles = all.filter((c) => c.status === "active" && !isCycleExpired(c));
        setReviewCycles(openCycles);
        if (openCycles.length) {
          const defaultCycle = openCycles.find((c) => c.status === "active") || openCycles[0];
          setForm((f) => ({
            ...f,
            assessmentPeriod: f.assessmentPeriod && openCycles.some((c) => c.name === f.assessmentPeriod)
              ? f.assessmentPeriod
              : defaultCycle.name,
          }));
        } else {
          setForm((f) => ({ ...f, assessmentPeriod: "" }));
        }
      })
      .catch(() => toast.error("Failed to load review cycles"))
      .finally(() => setCyclesLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const missingRatings = () => {
    const keys = [...RATING_FIELDS.map((f) => f.key), "overallRating"];
    return keys.filter((k) => !(Number(form[k]) >= 1 && Number(form[k]) <= ratingScale));
  };

  const goNext = () => {
    if (step === 0) {
      const missing = RATING_FIELDS.filter(
        (f) => !(Number(form[f.key]) >= 1 && Number(form[f.key]) <= ratingScale)
      );
      if (missing.length) {
        setAttempted(true);
        return toast.error(`Please rate all competencies (1–${ratingScale}) before continuing`);
      }
      if (!form.assessmentPeriod) {
        setAttempted(true);
        return toast.error("Please select an assessment period");
      }
      if (!form.employeeID) {
        setAttempted(true);
        return toast.error("Employee ID required");
      }
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setAttempted(true);
    if (!form.employeeID) {
      setEmpIdError("Employee ID is required");
      return toast.error("Employee ID required");
    }
    if (!form.assessmentPeriod) {
      return toast.error("Please select an assessment period");
    }
    const incomplete = missingRatings();
    if (incomplete.length) {
      if (incomplete.some((k) => k !== "overallRating")) {
        setStep(0);
      }
      return toast.error(`Please rate all competencies and overall self-rating (1–${ratingScale})`);
    }
    setEmpIdError("");
    setLoading(true);
    try {
      const payload = {
        ...form,
        workQuality: Number(form.workQuality),
        productivity: Number(form.productivity),
        communicationSkills: Number(form.communicationSkills),
        teamwork: Number(form.teamwork),
        problemSolvingSkills: Number(form.problemSolvingSkills),
        attendancePunctuality: Number(form.attendancePunctuality),
        overallRating: Number(form.overallRating),
      };
      await api.post("/api/feedback/selfassesmentform", payload);
      toast.success("Self-assessment submitted");
      nav("/my-self-assessments");
    } catch (err) {
      const msg = extractErrorMessage(err);
      if (msg && msg.toLowerCase().includes("employee")) {
        setEmpIdError(msg);
      }
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const content = () => {
    if (step === 0) return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={<>Employee <span style={{ color: "red" }}>*</span></>}
            value={employeeDisplay}
            InputProps={{ readOnly: true }}
            required
            error={!!(attempted && !form.employeeID) || !!empIdError}
            helperText={
              empIdError
              || (attempted && !form.employeeID ? "Employee is required" : "")
              || "Self-assessment is submitted for your own profile"
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            required
            label="Assessment Period *"
            value={form.assessmentPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
            disabled={cyclesLoading || reviewCycles.length === 0}
            error={attempted && !form.assessmentPeriod}
            helperText={
              cyclesLoading
                ? "Loading review cycles…"
                : reviewCycles.length === 0
                  ? "No open review cycles available. Expired periods are hidden."
                  : attempted && !form.assessmentPeriod
                    ? "Assessment period is required"
                    : `Select the review cycle for this assessment · Rating scale: 1–${ratingScale}`
            }
          >
            {sortByLabel(reviewCycles, (c) => c.name).map((c) => (
              <MenuItem key={c._id} value={c.name}>
                {c.name}
                {c.status !== "active" ? ` (${c.status})` : ""}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {RATING_FIELDS.map((f) => (
          <Grid item xs={12} sm={6} key={f.key}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: attempted && !(Number(form[f.key]) >= 1) ? "error.main" : "divider",
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                {f.label} <span style={{ color: "red" }}>*</span>
              </Typography>
              <Rating
                key={`${f.key}-${ratingScale}`}
                size="large"
                max={ratingScale}
                value={Number(form[f.key]) || 0}
                onChange={(_, v) => set(f.key, v || 0)}
              />
              <Typography variant="caption" color={attempted && !(Number(form[f.key]) >= 1) ? "error" : "text.secondary"}>
                {attempted && !(Number(form[f.key]) >= 1)
                  ? `Required · rate 1 to ${ratingScale}`
                  : `Rate 1 to ${ratingScale}`}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
    if (step === 1) return (
      <Stack spacing={2}>
        <RichTextEditor compact minHeight={110} label="Key achievements" value={form.achievements} onChange={(html) => set("achievements", html)} />
        <RichTextEditor compact minHeight={110} label="Your strengths" value={form.strengths} onChange={(html) => set("strengths", html)} />
        <TextField fullWidth multiline minRows={3} label="Learning activities undertaken" value={form.learningActivities} onChange={(e) => set("learningActivities", e.target.value)} />
      </Stack>
    );
    if (step === 2) return (
      <Stack spacing={2}>
        <RichTextEditor compact minHeight={110} label="Skills to improve" value={form.skillsToImprove} onChange={(html) => set("skillsToImprove", html)} />
        <RichTextEditor compact minHeight={110} label="Challenges faced" value={form.challengesFaced} onChange={(html) => set("challengesFaced", html)} />
        <TextField fullWidth multiline minRows={3} label="Self-improvement efforts" value={form.selfImprovementEfforts} onChange={(e) => set("selfImprovementEfforts", e.target.value)} />
        <TextField fullWidth multiline minRows={2} label="Support needed" value={form.supportNeeded} onChange={(e) => set("supportNeeded", e.target.value)} />
      </Stack>
    );
    return (
      <Stack spacing={2}>
        <TextField fullWidth multiline minRows={2} label="Short-term goals (3-6 months)" value={form.shortTermGoals} onChange={(e) => set("shortTermGoals", e.target.value)} />
        <TextField fullWidth multiline minRows={2} label="Long-term goals (1-3 years)" value={form.longTermGoals} onChange={(e) => set("longTermGoals", e.target.value)} />
        <TextField fullWidth multiline minRows={2} label="Career aspirations" value={form.careerAspirations} onChange={(e) => set("careerAspirations", e.target.value)} />
        <TextField fullWidth select label="Preferred Work Style" value={form.preferredWorkStyle} onChange={(e) => set("preferredWorkStyle", e.target.value)}>
          {sortByLabel(WORK_STYLES).map((s) => <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>)}
        </TextField>
        <TextField fullWidth multiline minRows={2} label="Feedback on management / team" value={form.feedbackOnManagement} onChange={(e) => set("feedbackOnManagement", e.target.value)} />
        <RichTextEditor compact minHeight={110} label="Additional comments" value={form.employeeComments} onChange={(html) => set("employeeComments", html)} />
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#F4EDFA", border: "1px solid", borderColor: attempted && !(Number(form.overallRating) >= 1) ? "error.main" : "transparent" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
            Overall self-rating <span style={{ color: "red" }}>*</span>
          </Typography>
          <Rating
            key={`overall-${ratingScale}`}
            size="large"
            max={ratingScale}
            value={Number(form.overallRating) || 0}
            onChange={(_, v) => set("overallRating", v || 0)}
          />
          <Typography variant="caption" color={attempted && !(Number(form.overallRating) >= 1) ? "error" : "text.secondary"}>
            {attempted && !(Number(form.overallRating) >= 1)
              ? `Required · rate 1 to ${ratingScale}`
              : `Rate 1 to ${ratingScale}`}
          </Typography>
        </Box>
      </Stack>
    );
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Self-Assessment"
        subtitle="Reflect on your work and set growth goals"
        icon={<AssignmentIndOutlinedIcon />}
      />

      <SectionCard>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
          {STEPS.map((l) => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
        </Stepper>

        <Box sx={{ mb: 4 }}>{content()}</Box>

        <Stack direction="row" justifyContent="space-between" alignItems="center" pt={2} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
          <Button startIcon={<ArrowBackIcon />} disabled={step === 0} onClick={() => setStep((s) => s - 1)}>Back</Button>
          <Typography variant="caption" color="text.secondary">Step {step + 1} of {STEPS.length}</Typography>
          {step < STEPS.length - 1 ? (
            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={goNext}>Next</Button>
          ) : (
            <Button
              variant="contained" color="success"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
              disabled={loading || !form.employeeID || !form.assessmentPeriod}
              onClick={submit}
            >
              {loading ? "Submitting…" : "Submit Assessment"}
            </Button>
          )}
        </Stack>
      </SectionCard>
    </Box>
  );
}
